import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Text,
  VStack,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Tooltip,
  Link,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Select,
  SimpleGrid,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useEffect, useState, useRef } from 'react';
import {
  FaBell,
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
  FaExternalLinkAlt,
  FaLeaf,
  FaSync,
  FaTimes,
} from 'react-icons/fa';
import { getOutingAvailability } from '../../brain/api';
import {
  buildRecreationGovPermitBookingUrlForOuting,
  isRecreationGovLandPermitEntity,
} from '../../utils/recreationGovPermitUrl';
import { buildDocGreatWalkBookingUrlForTrack } from '../../utils/docGreatWalkUrl';
import { buildHawaiiSpBookingUrlForOuting } from '../../utils/hawaiiSpUrl';
import { buildCaliSpBookingUrlForOuting } from '../../utils/caliSpUrl';
import { buildArizonaSpBookingUrlForOuting } from '../../utils/arizonaSpUrl';
import {
  format,
  addDays,
  parseISO,
  isSameDay,
  startOfDay,
  isBefore,
  max,
} from 'date-fns';

/** W/O Trek season window: Oct 1 – Apr 30 (matches backend worker). */
function getTorresDelPaineSeasonStartOct1(referenceDate = new Date()) {
  const year = referenceDate.getFullYear();
  const month = referenceDate.getMonth();

  if (month >= 9) {
    return new Date(year, 9, 1);
  }
  if (month <= 3) {
    return new Date(year - 1, 9, 1);
  }
  return new Date(year, 9, 1);
}

function getTorresDelPaineCalendarMinStart(referenceDate = new Date()) {
  const seasonOct1 = startOfDay(getTorresDelPaineSeasonStartOct1(referenceDate));
  const today = startOfDay(referenceDate);
  return format(max([today, seasonOct1]), 'yyyy-MM-dd');
}

/** Hut/campground slugs plus the park/trek parents — all share the Oct–Apr season. */
const TORRES_DEL_PAINE_CALENDAR_SLUGS = new Set([
  'torres-del-paine-national-park',
  'torres-del-paine-w-trek',
  'torres-del-paine-o-circuit',
  'torres-central-and-norte',
  'chileno',
  'seron',
  'frances',
  'cuernos',
  'los-perros',
  'dickson',
  'grey',
  'paine-grande',
]);

function isTorresDelPaineAvailabilitySlug(slug) {
  return TORRES_DEL_PAINE_CALENDAR_SLUGS.has(slug);
}

/** Alta Via 1 hut season: roughly mid-June through late September. */
function getAltaVia1CalendarMinStart(referenceDate = new Date()) {
  const today = startOfDay(referenceDate);
  const year = today.getFullYear();
  const seasonStart = startOfDay(new Date(year, 5, 15));
  const seasonEnd = startOfDay(new Date(year, 8, 30));
  if (today >= seasonStart && today <= seasonEnd) {
    return format(today, 'yyyy-MM-dd');
  }
  if (today < seasonStart) {
    return format(seasonStart, 'yyyy-MM-dd');
  }
  return format(new Date(year + 1, 5, 15), 'yyyy-MM-dd');
}

/**
 * Build a Campspot `guests` path segment, e.g. `guests0,2,0,0` for 2 adults.
 * Mirrors the backend crawler's buildGuestsParam so booking links pre-fill the
 * same party size the tracker searches with. Defaults to 1 adult.
 */
function buildCampspotGuestsParam(groupSize) {
  const adults = Math.max(1, Number(groupSize) || 1);
  return `guests0,${adults},0,0`;
}

export function resolveAvailabilityCalendarStartDate(slug, initialStartDate) {
  if (isTorresDelPaineAvailabilitySlug(slug)) {
    const minStart = getTorresDelPaineCalendarMinStart();
    if (!initialStartDate || initialStartDate < minStart) return minStart;
    return initialStartDate;
  }
  if (initialStartDate) return initialStartDate;
  if (slug === 'alta-via-1' || slug === 'alta-via-1-dolomites') {
    return getAltaVia1CalendarMinStart();
  }
  return format(new Date(), 'yyyy-MM-dd');
}

function isIncaToursOutingFromData(data) {
  const outing = data?.outing;
  return outing?.crawlerType === 'inca-trail' || outing?.source === 'inca-tours';
}

function isAntelopeCanyonOutingFromData(data) {
  const outing = data?.outing;
  return outing?.crawlerType === 'upper-antelope' || outing?.source === 'upper-antelope';
}

function formatSlotTooltipSuffix(place, dateStr) {
  const slots = place?.slotsByDate?.[dateStr];
  if (!slots?.length) return '';
  const parts = slots
    .map((s) => `${(s.time || '').slice(0, 5)}: ${s.spots}`)
    .join(', ');
  return ` — ${parts}`;
}

// Hut order for each Great Walk (in the order hikers encounter them)
const HUT_ORDER = {
  'Alta Via 1 (Dolomites)': [
    'Rifugio Biella',
    'Rifugio Fanes',
    'Rifugio Lavarella',
    'Rifugio Lagazuoi',
    'Rifugio Città di Fiume',
    'Rifugio Passo Staulanza',
    'Rifugio Coldai',
    'Rifugio Attilio Tissi',
    'Rifugio Vazzoler',
    'Rifugio Carestiato',
  ],
  'Milford Track': ['Clinton Hut', 'Mintaro Hut', 'Dumpling Hut'],
  'Routeburn Track': [
    'Routeburn Flats Hut',
    'Routeburn Flats Campsite',
    'Routeburn Falls Hut',
    'Lake Mackenzie Hut',
    'Lake Mackenzie Campsite',
  ],
  'Kepler Track': [
    'Brod Bay Campsite',
    'Luxmore Hut',
    'Iris Burn Hut',
    'Iris Burn Campsite',
    'Moturau Hut',
  ],
  'Tongariro Northern Circuit': [
    'Mangatepopo Hut',
    'Mangatepopo Campsite',
    'Oturere Hut',
    'Oturere Campsite',
    'Waihohonu Hut',
    'Waihohonu Campsite',
  ],
  'Abel Tasman Coast Track': [
    'Tinline Campsite',
    'Coquille Bay Campsite',
    'Apple Tree Bay Campsite',
    'Akersten Bay Campsite',
    'Observation Beach Campsite',
    'Watering Cove Campsite',
    'Te Pukatea Bay Campsite',
    'Anchorage Campsite',
    'Anchorage Hut',
    'Torrent Bay Village Campsite',
    'Bark Bay Campsite',
    'Bark Bay Hut',
    'Mosquito Bay Campsite (boat only)',
    'Onetahuti Bay Campsite',
    'Awaroa Campsite',
    'Awaroa Hut',
    'Waiharakeke Bay Campsite',
    'Totaranui Great Walk Campsite',
    'Anapai Bay Campsite',
    'Mutton Cove Campsite',
    'Whariwharangi Bay Campsite',
    'Whariwharangi Hut',
  ],
  'Heaphy Track': [
    'Brown Hut',
    'Brown Campsite',
    'Aorere Campsite',
    'Perry Saddle Hut',
    'Perry Saddle Campsite',
    'Gouland Downs Hut',
    'Gouland Downs Campsite',
    'Saxon Hut',
    'Saxon Campsite',
    'James Mackay Hut',
    'James Mackay Campsite',
    'Heaphy Hut',
    'Heaphy Campsite',
    'Katipo Creek Shelter Campsite',
    'Scotts Beach Campsite',
  ],
  'Rakiura Track': [
    'Maori Beach Campsite',
    'Port William Hut',
    'Port William Campsite',
    'North Arm Hut',
    'North Arm Campsite',
  ],
  // Prod outing name is "Lake Waikaremoana Track"
  'Lake Waikaremoana Track': [
    'Panekire Hut',
    'Waiopaoa Hut',
    'Waiopaoa Campsite',
    'Korokoro Campsite',
    'Maraunui Campsite',
    'Marauiti Hut',
    'Waiharuru Hut',
    'Waiharuru Campsite',
    'Tapuaenui Campsite',
    'Whanganui Hut',
  ],
  'Lake Waikaremoana': [
    'Panekire Hut',
    'Waiopaoa Hut',
    'Waiopaoa Campsite',
    'Korokoro Campsite',
    'Maraunui Campsite',
    'Marauiti Hut',
    'Waiharuru Hut',
    'Waiharuru Campsite',
    'Tapuaenui Campsite',
    'Whanganui Hut',
  ],
  'Paparoa Track': ['Ces Clark Hut', 'Moonlight Tops Hut', 'Pororari Hut'],
  'Whanganui Journey': [
    'Ōhinepane Campsite',
    'Poukaria Campsite',
    'Maharanui Campsite',
    'Whakahoro Campsite',
    'Whakahoro Bunkroom',
    'Mangapapa Campsite',
    'Ōhauora Campsite',
    'John Coull Hut',
    'John Coull Campsite',
    'Mangawaiiti Campsite',
    'Mangapurua Campsite',
    'Tīeke Kāinga Hut',
    'Tīeke Campsite',
    'Ngāporo Campsite',
  ],
  // Torres del Paine W-Trek huts (in typical W-Trek order, west to east)
  'Torres del Paine National Park': [
    'Grey',
    'Paine Grande',
    'Francés',
    'Cuernos',
    'Chileno',
    'Torres Central & Norte',
    'Serón',
    'Dickson',
    'Los Perros',
  ],
  // Phantom Ranch lodging types (lottery site order)
  'Phantom Ranch Cabin + Dorms': ['Cabin', 'Male Dorm', 'Female Dorm'],
  'Phantom Ranch': ['Cabin', 'Male Dorm', 'Female Dorm'],
};

export default function AvailabilityCalendar({
  slug,
  displayName,
  initialStartDate,
  daysToShow: propDaysToShow = 12,
  embedMode = false,
  accentColor = 'green',
  showButtons = false,
  enableBookingLinks = false,
  showCheckmarks = false,
  showFallbackOnError = false,
  groupSize,
  campsiteIds,
  placeNames,
  /** When false, drop the outer card chrome (border/shadow) — for nesting in a modal. */
  framed = true,
  /** Prefetched availability payload — skips the initial network fetch when set. */
  initialData = null,
  /** Extra prefetched windows keyed by startDate (yyyy-MM-dd). */
  prefetchedWindows = null,
}) {
  const [data, setData] = useState(initialData || null);
  const [initialLoading, setInitialLoading] = useState(!initialData);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(() =>
    resolveAvailabilityCalendarStartDate(slug, initialStartDate)
  );
  const [selectedPlaceName, setSelectedPlaceName] = useState(
    () => initialData?.places?.[0]?.name || null
  );
  const tableRef = useRef(null);
  const hasFetched = useRef(!!initialData);
  const resolvedInitialStart = resolveAvailabilityCalendarStartDate(
    slug,
    initialStartDate
  );
  const windowCacheRef = useRef({
    ...(prefetchedWindows || {}),
    ...(initialData ? { [resolvedInitialStart]: initialData } : {}),
  });
  const inFlightRef = useRef({});
  const minPartySize = Math.max(1, Number(groupSize) || 1);
  const campsiteIdsKey = Array.isArray(campsiteIds)
    ? campsiteIds.join(',')
    : '';

  const displayCheckmarks =
    showCheckmarks ||
    data?.outing?.source === 'havasupai' ||
    data?.outing?.crawlerType === 'havasupai' ||
    data?.outing?.source === 'grand-canyon-national-park-lodges' ||
    data?.outing?.crawlerType === 'phantom-ranch' ||
    slug === 'phantom-ranch';

  // Responsive: mobile shows calendar view, desktop shows grid with configurable days
  // Use fallback: false to prevent undefined on initial render
  const isMobile =
    useBreakpointValue({ base: true, md: false }, { fallback: 'md' }) ?? false;

  // Mobile: Calculate days to always end on Saturday for consistent 2-row layout
  // Formula: 14 cells total - leading days (day of week) = active days
  // This ensures leadingDays + activeDays = 14 (2 complete rows), ending on Saturday
  const getMobileDaysToShow = () => {
    const startDayOfWeek = parseISO(startDate).getDay(); // 0=Sun, 6=Sat
    return 14 - startDayOfWeek;
  };
  const daysToShow = isMobile ? getMobileDaysToShow() : propDaysToShow;

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headerBg = useColorModeValue('gray.50', 'gray.700');
  const todayBg = useColorModeValue('blue.50', 'blue.900');

  // Use accent color for themed elements in embed mode
  const themeColor = embedMode ? accentColor : 'green';

  // Merge any newly arrived background windows from the TrackWidget prefetch.
  useEffect(() => {
    if (!prefetchedWindows) return;
    windowCacheRef.current = {
      ...windowCacheRef.current,
      ...prefetchedWindows,
    };
  }, [prefetchedWindows]);

  const prefetchNextWindow = (fromStart) => {
    if (!slug || !fromStart) return;
    const nextStart = format(
      addDays(parseISO(fromStart), daysToShow),
      'yyyy-MM-dd'
    );
    if (windowCacheRef.current[nextStart] || inFlightRef.current[nextStart]) {
      return;
    }
    inFlightRef.current[nextStart] = true;
    const endDate = format(
      addDays(parseISO(nextStart), Math.max(daysToShow, 14)),
      'yyyy-MM-dd'
    );
    getOutingAvailability({
      slug,
      startDate: nextStart,
      endDate,
      groupSize: minPartySize,
      campsiteIds: campsiteIds?.length ? campsiteIds : undefined,
    })
      .then((response) => {
        if (response?.success?.places?.length > 0) {
          windowCacheRef.current[nextStart] = response.success;
        }
      })
      .catch(() => {
        // Background prefetch failures are non-fatal.
      })
      .finally(() => {
        delete inFlightRef.current[nextStart];
      });
  };

  // Fetch on mount and when date range changes — prefer cache, then network.
  useEffect(() => {
    if (!slug) return;

    const cached = windowCacheRef.current[startDate];
    if (cached) {
      hasFetched.current = true;
      setData(cached);
      setInitialLoading(false);
      setIsRefreshing(false);
      setError(null);
      if (cached.places?.length > 0 && !selectedPlaceName) {
        setSelectedPlaceName(cached.places[0].name);
      }
      prefetchNextWindow(startDate);
      return;
    }

    // Skip if this is just the breakpoint stabilizing on initial load
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchAvailability(true);
      return;
    }

    // Subsequent fetches when startDate / party / campsite filter changes
    fetchAvailability(false);
  }, [slug, startDate, minPartySize, campsiteIdsKey, daysToShow]);

  const fetchAvailability = async (isInitial = false) => {
    if (isInitial) {
      setInitialLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);
    try {
      // The API treats endDate as an EXCLUSIVE upper bound (its loops run
      // `while (date < end)`), while the grid renders `daysToShow` columns
      // starting at startDate (inclusive). Request start + daysToShow so the
      // final visible column (start + daysToShow - 1) is actually covered —
      // otherwise the last date always comes back missing and renders as 0.
      const currentEndDate = format(
        addDays(parseISO(startDate), daysToShow),
        'yyyy-MM-dd'
      );
      const response = await getOutingAvailability({
        slug,
        startDate,
        endDate: currentEndDate,
        groupSize: minPartySize,
        campsiteIds: campsiteIds?.length ? campsiteIds : undefined,
      });
      if (response?.success) {
        windowCacheRef.current[startDate] = response.success;
        setData(response.success);
        if (response.success.places?.length > 0 && !selectedPlaceName) {
          setSelectedPlaceName(response.success.places[0].name);
        }
        prefetchNextWindow(startDate);
      }
    } catch (err) {
      console.error('Error fetching availability:', err);
      setError('Failed to load availability data. Please try again later.');
    } finally {
      setInitialLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchAvailability(false);
  };

  const isAvailableForParty = (available) =>
    Number(available) >= minPartySize;

  const getAvailabilityColor = (available) => {
    return isAvailableForParty(available) ? 'green.500' : 'red.500';
  };

  const getAvailabilityBg = (available) => {
    return isAvailableForParty(available) ? 'green.100' : 'red.100';
  };

  const getBookingInfo = (checkinDate) => {
    const outingName = data?.outing?.name || '';
    const outingSource = data?.outing?.source || '';
    const entityId = data?.outing?.entity_id || '';
    const entityType = data?.outing?.entity_type || data?.outing?.type || '';
    const crawlerType = data?.outing?.crawlerType || '';

    // Inca Trail & Machu Picchu (inca.tours) — before generic timedentry_tour → Recreation.gov
    if (isIncaToursOutingFromData(data)) {
      return {
        url: 'https://inca.tours/en/USD/inca-trail-permit-availability',
        label: 'Inca Tours',
      };
    }

    if (isAntelopeCanyonOutingFromData(data)) {
      return {
        url:
          data?.outing?.official_site_url ||
          'https://navajonationparks.org/guided-tour-operators/antelope-canyon-tour-operators/',
        label: 'Book with operator',
      };
    }

    // Ningaloo Reef whale-shark swim tours (multi-operator, Rezdy). Checked
    // before the generic timedentry_tour → Recreation.gov branch. Inventory is
    // per operator boat, so link to the reef operator directory / official site.
    if (
      outingSource === 'ningaloo-whale-shark' ||
      crawlerType === 'ningaloo-whale-shark'
    ) {
      return {
        url:
          data?.outing?.official_site_url ||
          'https://www.australiascoralcoast.com/see-do/swim-ningaloo-whale-sharks',
        label: 'Book whale shark tour',
      };
    }

    // Recreation.gov timed entry tours (Haleakala Sunrise, etc.)
    if (entityType === 'tour' || entityType === 'timedentry_tour') {
      const parentId = data?.outing?.parent_id || '';
      return {
        url: parentId
          ? `https://www.recreation.gov/ticket/facility/${parentId}`
          : 'https://www.recreation.gov',
        label: 'Recreation.gov',
      };
    }

    // Recreation.gov permits (Brooks Camp, Inyo, etc.)
    if (
      (outingSource === 'recreation' || outingSource === 'recreation.gov') &&
      entityType === 'permit'
    ) {
      if (!entityId) {
        return { url: 'https://www.recreation.gov', label: 'Recreation.gov' };
      }
      const dateStr = checkinDate ? format(checkinDate, 'yyyy-MM-dd') : null;
      if (dateStr || isRecreationGovLandPermitEntity(entityId)) {
        return {
          url: buildRecreationGovPermitBookingUrlForOuting({
            outing: data?.outing,
            date: dateStr,
          }),
          label: 'Recreation.gov',
        };
      }
      return {
        url: `https://www.recreation.gov/permits/${entityId}`,
        label: 'Recreation.gov',
      };
    }

    // Recreation.gov campgrounds (US)
    if (outingSource === 'recreation' || outingSource === 'recreation.gov') {
      return {
        url: entityId
          ? `https://www.recreation.gov/camping/campgrounds/${entityId}`
          : 'https://www.recreation.gov',
        label: 'Recreation.gov',
      };
    }

    // Hawaii State Parks - Old system (camping.ehawaii.gov)
    if (crawlerType === 'hawaii') {
      return {
        url: 'https://camping.ehawaii.gov',
        label: 'Hawaii State Parks',
      };
    }

    // Hawaii State Parks - New system (Explore Outdoor Hawaii)
    if (crawlerType === 'hawaii-sp') {
      return {
        url: buildHawaiiSpBookingUrlForOuting({ outing: data?.outing }),
        label: 'Explore Outdoor Hawaiʻi',
      };
    }

    // Arizona State Parks. Campgrounds open #!park/{placeId}. Tours stay on
    // the tour search page; a matched departure uses Details.aspx?session_id=
    // in the alert, which this calendar click does not have.
    if (crawlerType === 'arizona-sp' || outingSource === 'arizona-sp') {
      return {
        url: buildArizonaSpBookingUrlForOuting({ outing: data?.outing }),
        label: 'Arizona State Parks',
      };
    }

    // California State Parks (ReserveCalifornia)
    if (crawlerType === 'cali-sp' || outingSource === 'cali-sp') {
      const dateStr = checkinDate
        ? format(checkinDate, 'yyyy-MM-dd')
        : null;
      return {
        url: buildCaliSpBookingUrlForOuting({
          outing: data?.outing,
          date: dateStr,
          nights: 1,
        }),
        label: 'ReserveCalifornia',
      };
    }

    // Dry Tortugas (Yankee Freedom Ferry)
    if (crawlerType === 'dry-tortugas' || outingSource === 'dry-tortugas') {
      // Day trip vs camping trip URLs
      const isDayTrip = outingName.toLowerCase().includes('day trip');
      return {
        url: isDayTrip
          ? 'https://www.drytortugas.com/day-trip-ferry-tickets/'
          : 'https://www.drytortugas.com/overnight-camping-reservations/',
        label: 'Yankee Freedom',
      };
    }

    // Parks Canada outings (v2)
    if (
      crawlerType === 'parks-canada-v2' ||
      crawlerType === 'parks-canada-v2-permits' ||
      outingName.includes("Lake O'Hara") ||
      outingSource === 'parks-canada-v2'
    ) {
      return {
        url: 'https://reservation.pc.gc.ca/',
        label: 'Parks Canada',
      };
    }

    // Ontario Parks (Going to Camp)
    if (crawlerType === 'ontario-parks' || outingSource === 'ontario-parks') {
      return {
        url: 'https://reservations.ontarioparks.com/',
        label: 'Ontario Parks',
      };
    }

    // Havasupai (Newbook) — before Phantom Ranch; lodge is entity_type hotel
    if (outingSource === 'havasupai' || crawlerType === 'havasupai') {
      const bookingUrl =
        data?.outing?.metadata?.bookingUrl ||
        'https://bookingsus.newbook.cloud/online/havasupai';
      return {
        url: bookingUrl,
        label: 'Havasupai Reservations',
      };
    }

    // Phantom Ranch (Grand Canyon)
    if (outingSource === 'grand-canyon-national-park-lodges') {
      return {
        url: 'https://secure.phantomranchlottery.com/phantom-ranch-lottery/availability/check',
        label: 'Phantom Ranch Lottery',
      };
    }

    // Torres del Paine W-Trek (TorresHike)
    if (outingSource === 'torres-hike' || crawlerType === 'torres-hike') {
      return {
        url: 'https://torreshike.com/en/search?utm_source=reservenature.com&utm_medium=referal',
        label: 'TorresHike',
      };
    }

    if (outingSource === 'alta-via' || crawlerType === 'alta-via') {
      const bookingUrl =
        data?.outing?.metadata?.bookingUrl ||
        data?.outing?.metadata?.availabilityUrl ||
        'https://reservenature.com/blogs/alta-via-1-rifugios-booking-guide';
      return {
        url: bookingUrl,
        label: 'Book rifugio',
      };
    }

    // Baxter State Park (Roaring Brook campground + day-use trailhead parking)
    if (
      outingSource === 'baxter' ||
      crawlerType === 'baxter' ||
      crawlerType === 'baxter-parking'
    ) {
      const isParking = crawlerType === 'baxter-parking';
      return {
        url: isParking
          ? 'https://reservation.baxterstatepark.org/reserve/parking'
          : 'https://reservation.baxterstatepark.org/reserve/camping',
        label: 'Baxter State Park',
      };
    }

    // Campspot (e.g. Telluride Town Park) — deep-link into the search view
    // with a 1-night stay and pre-filled guests so users land on live
    // availability for that date, e.g.
    // .../book/telluride-2026/search/2026-07-01/2026-07-02/guests0,1,0,0/list
    if (outingSource === 'campspot' || crawlerType === 'campspot') {
      const baseUrl = (
        data?.outing?.metadata?.bookingUrl ||
        'https://www.campspot.com/book/telluride-2026'
      ).replace(/\/+$/, '');
      const checkin = checkinDate || getDefaultCampspotCheckin();
      const checkinStr = format(checkin, 'yyyy-MM-dd');
      const checkoutStr = format(addDays(checkin, 1), 'yyyy-MM-dd');
      const guestsParam = buildCampspotGuestsParam(
        groupSize ?? data?.outing?.metadata?.defaultGroupSize
      );
      return {
        url: `${baseUrl}/search/${checkinStr}/${checkoutStr}/${guestsParam}/list`,
        label: 'Campspot',
      };
    }

    // CampLife (e.g. Salt Creek Recreation Area)
    if (outingSource === 'camplife' || crawlerType === 'camplife') {
      const bookingUrl =
        data?.outing?.metadata?.bookingUrl ||
        'https://www.camplife.com/415/reservation/step1';
      return {
        url: bookingUrl,
        label: 'CampLife',
      };
    }

    // Glacier National Park backcountry chalets (Belton Chalets) — Sperry &
    // Granite Park. No online cart; reservations are via a request form / phone,
    // so link straight to each chalet's reservations page.
    if (outingSource === 'glacier-chalets' || crawlerType === 'glacier-chalets') {
      const bookingUrl =
        data?.outing?.metadata?.bookingUrl ||
        (outingName.toLowerCase().includes('granite')
          ? 'https://graniteparkchalet.com/reservations.php'
          : 'https://www.sperrychalet.com/reservations.php');
      return {
        url: bookingUrl,
        label: 'Belton Chalets',
      };
    }

    if (outingSource === 'resnexus' || crawlerType === 'resnexus') {
      const propertyGuid =
        data?.outing?.metadata?.propertyGuid || data?.outing?.entity_id;
      const bookingUrl =
        data?.outing?.metadata?.bookingUrl ||
        (propertyGuid
          ? `https://resnexus.com/resnexus/reservations/book/${propertyGuid}`
          : 'https://resnexus.com/resnexus/reservations/book/9C1C0985-2326-4A2C-A795-27F91D529311');
      return {
        url: bookingUrl,
        label: outingName || 'ResNexus',
      };
    }

    // West Virginia State Parks (Inntopia, e.g. Thorny Mountain Fire Tower,
    // Seneca cabins) — deep-link into the lodging shop page filtered to the
    // property on the clicked/first-available date. Some units enforce a
    // minimum stay (the pioneer cabins need 2 nights), so honor
    // metadata.calendarStayNights for the checkout date; otherwise a 1-night
    // link would land on an empty result.
    if (outingSource === 'wv-sp' || crawlerType === 'wv-sp') {
      const meta = data?.outing?.metadata || {};
      const salesId = meta.salesId || 7816932;
      const destination = meta.destination || 774;
      const stayNights = Math.max(1, Number(meta.calendarStayNights) || 1);
      const checkin = checkinDate || getDefaultCampspotCheckin();
      const checkinStr = format(checkin, 'yyyy-MM-dd');
      const checkoutStr = format(addDays(checkin, stayNights), 'yyyy-MM-dd');
      return {
        url: `https://book.wvstateparks.com/Ecomm/Shop/Lodging/${salesId}/en-US/?arrivaldate=${checkinStr}&departuredate=${checkoutStr}&adultcount=2&childCount=0&childagearray=&destination=${destination}`,
        label: 'WV State Parks',
      };
    }

    // DOC Great Walks (NZ)
    if (outingSource === 'great-walks' || crawlerType === 'great-walks') {
      const trackName = data?.outing?.parent_name || outingName;
      const arrivalDateStr = checkinDate
        ? format(checkinDate, 'yyyy-MM-dd')
        : format(getDefaultCampspotCheckin(), 'yyyy-MM-dd');
      const partySize = Math.max(1, Number(groupSize) || 1);
      return {
        url: buildDocGreatWalkBookingUrlForTrack({
          trackName,
          arrivalDate: arrivalDateStr,
          totalParty: partySize,
        }),
        label: 'DOC',
      };
    }

    return {
      url: data?.outing?.official_site_url || 'https://www.recreation.gov',
      label: 'Book',
    };
  };

  // Pick a sensible default check-in for booking links that aren't tied to a
  // specific clicked date (header/CTA buttons, fallback link): the first
  // visible date with availability, else the first visible date, else today.
  const getDefaultCampspotCheckin = () => {
    const columns = getDateColumns();
    const place =
      getSortedPlaces().find((p) => p.name === selectedPlaceName) ||
      getSortedPlaces()[0];
    const firstAvailable = columns.find((date) =>
      isAvailableForParty(
        place?.availability?.[format(date, 'yyyy-MM-dd')] ?? 0
      )
    );
    return firstAvailable || columns[0] || startOfDay(new Date());
  };

  // Handle clicking on a date to open booking website
  const handleDateClick = (clickedDate) => {
    if (!enableBookingLinks) return;
    const bookingUrl = getBookingInfo(clickedDate).url;
    window.open(bookingUrl, '_blank', 'noopener,noreferrer');
  };

  const getSortedPlaces = () => {
    if (!data?.places) return [];
    const trackName = data.outing?.name;
    const hutOrder = HUT_ORDER[trackName];
    const nameFilter =
      Array.isArray(placeNames) && placeNames.length > 0
        ? new Set(placeNames)
        : null;
    const matchesPlaceFilter = (p) =>
      !nameFilter || nameFilter.has(p.name);

    if (!hutOrder) {
      // Include PERMIT, HOTEL, FERRY, TOUR, REFUGIO types for various outings
      const tours = data.places
        .filter((p) => p.type === 'TOUR')
        .filter(matchesPlaceFilter)
        .sort((a, b) => a.name.localeCompare(b.name));
      const ferries = data.places
        .filter((p) => p.type === 'FERRY')
        .filter(matchesPlaceFilter)
        .sort((a, b) => a.name.localeCompare(b.name));
      const hotels = data.places
        .filter((p) => p.type === 'HOTEL')
        .filter(matchesPlaceFilter)
        .sort((a, b) => a.name.localeCompare(b.name));
      const permits = data.places
        .filter((p) => p.type === 'PERMIT')
        .filter(matchesPlaceFilter)
        .sort((a, b) => a.name.localeCompare(b.name));
      const refugios = data.places
        .filter((p) => p.type === 'REFUGIO')
        .filter(matchesPlaceFilter)
        .sort((a, b) => a.name.localeCompare(b.name));
      const huts = data.places
        .filter((p) => p.type === 'HUT')
        .filter(matchesPlaceFilter)
        .sort((a, b) => a.name.localeCompare(b.name));
      const campsites = data.places
        .filter((p) => p.type === 'CAMPSITE')
        .filter(matchesPlaceFilter)
        .sort((a, b) => a.name.localeCompare(b.name));
      return [
        ...tours,
        ...ferries,
        ...hotels,
        ...permits,
        ...refugios,
        ...huts,
        ...campsites,
      ];
    }

    const sorted = [];
    const remaining = data.places.filter(matchesPlaceFilter);
    hutOrder.forEach((hutName) => {
      const idx = remaining.findIndex(
        (p) =>
          p.name.includes(hutName) ||
          hutName.includes(p.name.replace(' Hut', ''))
      );
      if (idx !== -1) sorted.push(remaining.splice(idx, 1)[0]);
    });
    const permits = remaining
      .filter((p) => p.type === 'PERMIT')
      .sort((a, b) => a.name.localeCompare(b.name));
    const campsites = remaining
      .filter((p) => p.type === 'CAMPSITE')
      .sort((a, b) => a.name.localeCompare(b.name));
    const refugios = remaining
      .filter((p) => p.type === 'REFUGIO')
      .sort((a, b) => a.name.localeCompare(b.name));
    const otherHuts = remaining
      .filter((p) => p.type === 'HUT')
      .sort((a, b) => a.name.localeCompare(b.name));
    return [...sorted, ...otherHuts, ...refugios, ...campsites, ...permits];
  };

  const getDateColumns = () => {
    const dates = [];
    let currentDate = parseISO(startDate);
    for (let i = 0; i < daysToShow; i++) {
      dates.push(currentDate);
      currentDate = addDays(currentDate, 1);
    }
    return dates;
  };

  const navigateDates = (direction) => {
    const currentStart = parseISO(startDate);
    const shift = daysToShow; // Shift by the number of days shown
    const newStart = addDays(
      currentStart,
      direction === 'next' ? shift : -shift
    );
    const minStart =
      isTorresDelPaineAvailabilitySlug(slug) ||
      data?.outing?.source === 'torres-hike' ||
      data?.outing?.crawlerType === 'torres-hike'
        ? parseISO(getTorresDelPaineCalendarMinStart())
        : slug === 'alta-via-1' || slug === 'alta-via-1-dolomites'
          ? parseISO(getAltaVia1CalendarMinStart())
          : startOfDay(new Date());
    if (isBefore(newStart, minStart)) {
      setStartDate(format(minStart, 'yyyy-MM-dd'));
    } else {
      setStartDate(format(newStart, 'yyyy-MM-dd'));
    }
  };

  if (initialLoading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner
          size="xl"
          color={themeColor.includes('#') ? themeColor : `${themeColor}.500`}
        />
        <Text mt={4} color="gray.600">
          Loading availability...
        </Text>
      </Box>
    );
  }

  // By default, hide the component on error/no-data so most blogs keep their
  // existing "silently disappear" behavior. Blogs that wrap the calendar in a
  // dedicated section with its own heading can opt into a graceful fallback
  // (via showFallbackOnError) so a failed/slow load doesn't leave the heading
  // orphaned over an empty gap.
  if ((error || !data || !data.places || data.places.length === 0) && !showFallbackOnError) {
    return null;
  }

  // Graceful fallback when live availability can't be loaded (e.g. the upstream
  // booking system is slow or unreachable).
  if (error || !data || !data.places || data.places.length === 0) {
    // Only surface a booking link if we actually resolved the outing (so we
    // don't send users to a wrong/default site); otherwise just offer a retry.
    const fallbackBooking = data?.outing ? getBookingInfo() : null;
    return (
      <Box
        bg={framed ? bgColor : 'transparent'}
        borderRadius={framed ? 'xl' : 'none'}
        borderWidth={framed ? '1px' : '0'}
        borderColor={borderColor}
        p={framed ? [4, 6] : [1, 2]}
        boxShadow={framed ? 'sm' : 'none'}
        textAlign="center"
      >
        <VStack spacing={3}>
          <Icon as={FaLeaf} boxSize={6} color={`${themeColor}.500`} />
          <Heading size="sm" color="gray.800">
            {displayName || data?.outing?.name || 'Live availability'}
          </Heading>
          <Text fontSize="sm" color="gray.600" maxW="md">
            Live availability is taking longer than usual to load. Check the official booking
            site for current openings, or set a tracker and we’ll alert you the moment a spot
            frees up.
          </Text>
          <HStack spacing={3} flexWrap="wrap" justify="center">
            {fallbackBooking && (
              <Link href={fallbackBooking.url} isExternal _hover={{ textDecoration: 'none' }}>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  rightIcon={<FaExternalLinkAlt />}
                >
                  {fallbackBooking.label}
                </Button>
              </Link>
            )}
            <Button
              size="sm"
              leftIcon={<FaSync />}
              colorScheme={themeColor}
              onClick={handleRefresh}
              isLoading={isRefreshing}
            >
              Retry
            </Button>
          </HStack>
        </VStack>
      </Box>
    );
  }

  const sortedPlaces = getSortedPlaces();
  const dateColumns = getDateColumns();
  const today = startOfDay(new Date());
  const selectedPlace =
    sortedPlaces.find((p) => p.name === selectedPlaceName) || sortedPlaces[0];

  const getCellTooltip = (place, date, available, { includePlaceName = false } = {}) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const slotSuffix = formatSlotTooltipSuffix(place, dateStr);
    const status = displayCheckmarks
      ? isAvailableForParty(available)
        ? 'Available'
        : 'Sold out'
      : `${available} spot${available === 1 ? '' : 's'}`;
    const prefix = includePlaceName
      ? `${place.name} on ${format(date, 'MMM d')}: `
      : `${format(date, 'MMM d')}: `;
    return `${prefix}${status}${slotSuffix}${enableBookingLinks ? ' - Click to book' : ''}`;
  };

  // Mobile: Traditional calendar view with dropdown
  // Always shows exactly 2 rows (14 cells) ending on Saturday for consistent layout
  const MobileCalendarView = () => {
    // Calculate leading dates (days before startDate to complete the first week)
    const firstDate = parseISO(startDate);
    const leadingDaysCount = firstDate.getDay(); // 0 = Sunday, 6 = Saturday
    const leadingDates = Array.from({ length: leadingDaysCount }).map((_, i) =>
      addDays(firstDate, -(leadingDaysCount - i))
    );
    // No trailing dates needed - we always end on Saturday

    return (
      <VStack spacing={4} align="stretch">
        {/* Place selector dropdown - only show if multiple places */}
        {sortedPlaces.length > 1 && (
          <Select
            value={selectedPlaceName || ''}
            onChange={(e) => setSelectedPlaceName(e.target.value)}
            bg="white"
            size="sm"
          >
            {sortedPlaces.map((place) => (
              <option key={place.name} value={place.name}>
                {place.name} ({place.type})
              </option>
            ))}
          </Select>
        )}

        {/* Calendar grid - 7 columns for days of week */}
        <Box position="relative">
          {isRefreshing && (
            <Flex
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg="whiteAlpha.800"
              zIndex={10}
              align="center"
              justify="center"
              borderRadius="lg"
            >
              <Spinner size="lg" color="green.500" thickness="3px" />
            </Flex>
          )}
          <SimpleGrid columns={7} spacing={1}>
            {/* Day headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <Text
                key={day}
                fontSize="2xs"
                fontWeight="bold"
                textAlign="center"
                color="gray.500"
                py={1}
              >
                {day}
              </Text>
            ))}

            {/* Leading dates (grayed out, outside range) */}
            {leadingDates.map((date) => (
              <Flex
                key={`lead-${format(date, 'yyyy-MM-dd')}`}
                direction="column"
                align="center"
                justify="center"
                p={1}
                minH="44px"
                borderRadius="md"
                bg="gray.50"
              >
                <Text fontSize="xs" fontWeight="medium" color="gray.400">
                  {format(date, 'd')}
                </Text>
              </Flex>
            ))}

            {/* Active date cells */}
            {dateColumns.map((date) => {
              const dateStr = format(date, 'yyyy-MM-dd');
              const available = selectedPlace?.availability?.[dateStr] ?? 0;
              const isToday = isSameDay(date, today);

              return (
                <Tooltip
                  key={dateStr}
                  label={getCellTooltip(selectedPlace, date, available)}
                  hasArrow
                >
                  <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    p={1}
                    minH="44px"
                    borderRadius="md"
                    bg={getAvailabilityBg(available)}
                    borderWidth={isToday ? '2px' : '0'}
                    borderColor={isToday ? 'blue.400' : 'transparent'}
                    cursor={enableBookingLinks ? 'pointer' : 'default'}
                    onClick={() => handleDateClick(date)}
                    _hover={enableBookingLinks ? { transform: 'scale(1.05)', boxShadow: 'md' } : {}}
                    transition="all 0.2s"
                  >
                    <Text fontSize="xs" fontWeight="bold" color="gray.700">
                      {format(date, 'd')}
                    </Text>
                    {displayCheckmarks ? (
                      <Icon
                        as={isAvailableForParty(available) ? FaCheck : FaTimes}
                        color={getAvailabilityColor(available)}
                        boxSize={3}
                      />
                    ) : (
                      <Text
                        fontSize="2xs"
                        fontWeight="bold"
                        color={getAvailabilityColor(available)}
                      >
                        {available}
                      </Text>
                    )}
                  </Flex>
                </Tooltip>
              );
            })}
          </SimpleGrid>
        </Box>
      </VStack>
    );
  };

  // Desktop: Grid view with huts as rows
  const DesktopGridView = () => (
    <Box position="relative">
      {isRefreshing && (
        <Flex
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="whiteAlpha.800"
          zIndex={10}
          align="center"
          justify="center"
          borderRadius="lg"
        >
          <Spinner size="lg" color="green.500" thickness="3px" />
        </Flex>
      )}
      <TableContainer
        ref={tableRef}
        overflowX="auto"
        borderRadius="lg"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <Table size="sm" variant="simple">
          <Thead>
            <Tr bg={headerBg}>
              <Th
                position="sticky"
                left={0}
                bg={headerBg}
                zIndex={1}
                borderRightWidth="2px"
                borderRightColor={borderColor}
                w="180px"
                minW="180px"
                maxW="180px"
                fontSize="xs"
              >
                {embedMode ? '' : 'Accommodation'}
              </Th>
              {dateColumns.map((date) => {
                const isToday = isSameDay(date, today);
                return (
                  <Th
                    key={date.toISOString()}
                    textAlign="center"
                    px={2}
                    minW="50px"
                    bg={isToday ? todayBg : headerBg}
                    borderBottomWidth={isToday ? '2px' : '1px'}
                    borderBottomColor={isToday ? 'blue.400' : borderColor}
                  >
                    <VStack spacing={0}>
                      <Text fontSize="2xs" fontWeight="normal" color="gray.500">
                        {format(date, 'EEE')}
                      </Text>
                      <Text fontSize="xs" fontWeight="bold">
                        {format(date, 'd')}
                      </Text>
                      <Text fontSize="2xs" fontWeight="normal" color="gray.400">
                        {format(date, 'MMM')}
                      </Text>
                    </VStack>
                  </Th>
                );
              })}
            </Tr>
          </Thead>
          <Tbody>
            {sortedPlaces.map((place) => (
              <Tr key={place.name}>
                <Td
                  position="sticky"
                  left={0}
                  bg={bgColor}
                  zIndex={1}
                  borderRightWidth="2px"
                  borderRightColor={borderColor}
                  py={2}
                  w="180px"
                  minW="180px"
                  maxW="180px"
                >
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    lineHeight="short"
                    whiteSpace="normal"
                    wordBreak="break-word"
                  >
                    {place.name}
                  </Text>
                </Td>
                {dateColumns.map((date) => {
                  const dateStr = format(date, 'yyyy-MM-dd');
                  const available = place.availability?.[dateStr] ?? 0;
                  const isToday = isSameDay(date, today);
                  return (
                    <Td
                      key={dateStr}
                      textAlign="center"
                      px={1}
                      py={2}
                      bg={isToday ? todayBg : 'transparent'}
                    >
                      <Tooltip
                        label={getCellTooltip(place, date, available, {
                          includePlaceName: true,
                        })}
                        hasArrow
                        placement="top"
                      >
                        <Flex
                          w="36px"
                          h="36px"
                          mx="auto"
                          align="center"
                          justify="center"
                          borderRadius="md"
                          bg={getAvailabilityBg(available)}
                          cursor={enableBookingLinks ? 'pointer' : 'default'}
                          transition="all 0.2s"
                          onClick={() => handleDateClick(date)}
                          _hover={enableBookingLinks ? { transform: 'scale(1.1)', boxShadow: 'md' } : {}}
                        >
                          {displayCheckmarks ? (
                            <Icon
                              as={isAvailableForParty(available) ? FaCheck : FaTimes}
                              color={getAvailabilityColor(available)}
                              boxSize={4}
                            />
                          ) : (
                            <Text
                              fontSize="sm"
                              fontWeight="bold"
                              color={getAvailabilityColor(available)}
                            >
                              {available}
                            </Text>
                          )}
                        </Flex>
                      </Tooltip>
                    </Td>
                  );
                })}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Box
      bg={framed ? bgColor : 'transparent'}
      borderRadius={framed ? 'xl' : 'none'}
      borderWidth={framed ? '1px' : '0'}
      borderColor={borderColor}
      p={framed ? [3, 4] : 0}
      boxShadow={framed ? 'lg' : 'none'}
      overflow="hidden"
    >
      <VStack spacing={3} align="stretch">
        {/* Header - Embed mode: no header (title in table) | Regular: with buttons */}
        {!embedMode && (
          <>
            {/* Mobile: Title on top, buttons below */}
            <VStack spacing={2} display={['flex', 'flex', 'none']}>
              <VStack spacing={0} textAlign="center">
                <Heading size="md" color="gray.800" noOfLines={1}>
                  {displayName || data?.outing?.name || 'Live Availability'}
                </Heading>
                <Text fontSize="xs" color="gray.500">
                  Live Availability
                </Text>
              </VStack>
              <HStack spacing={2} justify="center">
                <Link
                  href={getBookingInfo().url}
                  isExternal
                  _hover={{ textDecoration: 'none' }}
                >
                  <Button
                    size="sm"
                    rightIcon={<FaExternalLinkAlt />}
                    variant="outline"
                    colorScheme="gray"
                  >
                    {getBookingInfo().label}
                  </Button>
                </Link>
                <Link
                  href={data?.outing?.permalink || '/'}
                  _hover={{ textDecoration: 'none' }}
                >
                  <Button
                    size="sm"
                    leftIcon={<FaBell />}
                    colorScheme={themeColor}
                  >
                    Set Up Alerts
                  </Button>
                </Link>
              </HStack>
            </VStack>

            {/* Desktop: Button | Title | Button in one row */}
            <Flex
              justify="space-between"
              align="center"
              display={['none', 'none', 'flex']}
            >
              <Link
                href={getBookingInfo().url}
                isExternal
                _hover={{ textDecoration: 'none' }}
              >
                <Button
                  size="sm"
                  rightIcon={<FaExternalLinkAlt />}
                  variant="outline"
                  colorScheme="gray"
                >
                  {getBookingInfo().label}
                </Button>
              </Link>

              <VStack spacing={0} textAlign="center" flex={1}>
                <Heading size="md" color="gray.800" noOfLines={1}>
                  {displayName || data?.outing?.name || 'Live Availability'}
                </Heading>
                <Text fontSize="xs" color="gray.500">
                  Live Availability
                </Text>
              </VStack>

              <Link
                href={data?.outing?.permalink || '/'}
                _hover={{ textDecoration: 'none' }}
              >
                <Button
                  size="sm"
                  leftIcon={<FaBell />}
                  colorScheme={themeColor}
                >
                  Set Up Alerts
                </Button>
              </Link>
            </Flex>
          </>
        )}

        {/* Header with buttons - shown in embed mode when showButtons is true */}
        {embedMode && showButtons && (
          <>
            {/* Mobile: Title on top, buttons below */}
            <VStack spacing={2} display={['flex', 'flex', 'none']}>
              <VStack spacing={0} textAlign="center">
                <Heading size="md" color="gray.800" noOfLines={1}>
                  {displayName || data?.outing?.name || 'Live Availability'}
                </Heading>
                <Text fontSize="xs" color="gray.500">
                  Live Availability
                </Text>
              </VStack>
              <HStack spacing={2} justify="center">
                <Link
                  href={getBookingInfo().url}
                  isExternal
                  _hover={{ textDecoration: 'none' }}
                >
                  <Button
                    size="sm"
                    rightIcon={<FaExternalLinkAlt />}
                    variant="outline"
                    colorScheme="gray"
                  >
                    Book on {getBookingInfo().label}
                  </Button>
                </Link>
                <Link
                  href={data?.outing?.permalink || '/'}
                  _hover={{ textDecoration: 'none' }}
                >
                  <Button
                    size="sm"
                    leftIcon={<FaBell />}
                    variant="outline"
                    colorScheme={themeColor}
                  >
                    Get Alerts
                  </Button>
                </Link>
              </HStack>
            </VStack>

            {/* Desktop: Button | Title | Button in one row */}
            <Flex
              justify="space-between"
              align="center"
              display={['none', 'none', 'flex']}
            >
              <Link
                href={getBookingInfo().url}
                isExternal
                _hover={{ textDecoration: 'none' }}
              >
                <Button
                  size="sm"
                  rightIcon={<FaExternalLinkAlt />}
                  variant="outline"
                  colorScheme="gray"
                >
                  Book on {getBookingInfo().label}
                </Button>
              </Link>

              <VStack spacing={0} textAlign="center" flex={1}>
                <Heading size="md" color="gray.800" noOfLines={1}>
                  {displayName || data?.outing?.name || 'Live Availability'}
                </Heading>
                <Text fontSize="xs" color="gray.500">
                  Live Availability
                </Text>
              </VStack>

              <Link
                href={data?.outing?.permalink || '/'}
                _hover={{ textDecoration: 'none' }}
              >
                <Button
                  size="sm"
                  leftIcon={<FaBell />}
                  variant="outline"
                  colorScheme={themeColor}
                >
                  Get Alerts
                </Button>
              </Link>
            </Flex>
          </>
        )}

        {/* Date Navigation - Embed mode: Name + arrows, dates on next line on mobile */}
        {embedMode ? (
          /* Embed: Column on mobile (stacked), row on desktop */
          <Flex
            direction={['column', 'row']}
            align={['stretch', 'center']}
            gap={2}
          >
            {/* Name - only show if buttons are not shown (otherwise title is in button header) */}
            {!showButtons && (
              <VStack align={['center', 'start']} spacing={0} flexShrink={0}>
                <Text fontWeight="bold" color="gray.800" fontSize="md">
                  {displayName || data?.outing?.name || 'Availability'}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  Live Availability
                </Text>
              </VStack>
            )}

            {/* Date navigation - centered on mobile, right-aligned on desktop (or centered when showButtons) */}
            <HStack
              spacing={4}
              flex={1}
              justify={showButtons ? 'center' : ['center', 'flex-end']}
            >
              <IconButton
                icon={<FaChevronLeft />}
                aria-label="Previous dates"
                onClick={() => navigateDates('prev')}
                size="xs"
                variant="ghost"
                isDisabled={
                  isRefreshing ||
                  isSameDay(parseISO(startDate), today) ||
                  isBefore(parseISO(startDate), today)
                }
              />
              <Text
                fontWeight="medium"
                color="gray.600"
                fontSize="sm"
                whiteSpace="nowrap"
              >
                {format(parseISO(startDate), 'MMM d')} -{' '}
                {format(
                  addDays(parseISO(startDate), daysToShow - 1),
                  'MMM d, yyyy'
                )}
              </Text>
              <IconButton
                icon={<FaChevronRight />}
                aria-label="Next dates"
                onClick={() => navigateDates('next')}
                size="xs"
                variant="ghost"
                isDisabled={isRefreshing}
              />
            </HStack>
          </Flex>
        ) : (
          /* Regular: Standard layout */
          <Flex justify="space-between" align="center">
            <IconButton
              icon={<FaChevronLeft />}
              aria-label="Previous dates"
              onClick={() => navigateDates('prev')}
              size="sm"
              variant="ghost"
              isDisabled={
                isRefreshing ||
                isSameDay(parseISO(startDate), today) ||
                isBefore(parseISO(startDate), today)
              }
            />
            <HStack spacing={2}>
              <Text fontWeight="medium" color="gray.600" fontSize="sm">
                {format(parseISO(startDate), 'MMM d')} -{' '}
                {format(
                  addDays(parseISO(startDate), daysToShow - 1),
                  'MMM d, yyyy'
                )}
              </Text>
              <Tooltip label="Refresh availability data" hasArrow>
                <IconButton
                  icon={<FaSync />}
                  aria-label="Refresh data"
                  onClick={handleRefresh}
                  size="xs"
                  variant="ghost"
                  colorScheme={themeColor}
                  isLoading={isRefreshing}
                />
              </Tooltip>
            </HStack>
            <IconButton
              icon={<FaChevronRight />}
              aria-label="Next dates"
              onClick={() => navigateDates('next')}
              size="sm"
              variant="ghost"
              isDisabled={isRefreshing}
            />
          </Flex>
        )}

        {/* Responsive View */}
        {isMobile ? <MobileCalendarView /> : <DesktopGridView />}

        {/* Footer - only show helper text in non-embed mode */}
        {!embedMode && (
          <Text fontSize="xs" color="gray.500" textAlign="center">
            {displayCheckmarks
              ? 'Green check = available, red X = sold out. Data updates regularly.'
              : 'Numbers indicate available spots. Data updates regularly.'}
          </Text>
        )}

        {/* Powered by Reserve Nature - only in embed mode - SEO backlink */}
        {embedMode && (
          <Flex justify="center" pt={1} fontSize="xs">
            <Link
              href={`https://reservenature.com?utm_source=embed&utm_medium=widget&utm_campaign=availability`}
              isExternal
              color={`${themeColor}.600`}
              _hover={{ color: `${themeColor}.700`, transform: 'scale(1.02)' }}
              fontWeight="medium"
              textShadow="0 1px 2px rgba(0,0,0,0.1)"
              transition="all 0.2s"
              textDecoration="none"
            >
              <HStack spacing={1}>
                <Icon as={FaLeaf} boxSize={3} mr={1} />
                <Text>Powered by Reserve Nature</Text>
              </HStack>
            </Link>
          </Flex>
        )}
      </VStack>
    </Box>
  );
}
